// T4c — ONE export mechanism for every list view.
//
// Survey, 2026-08-06: **26 pages render a <table>. Four could export.** The
// four that could each did it differently — Asset Register and Reports call a
// server endpoint, Audit Log streams a blob, Health builds its own CSV inline.
//
// The instruction for this tranche was explicit: do not invent a different
// interaction per page. So this is client-side by design — it serialises the
// rows the operator is ALREADY LOOKING AT, including whatever filters and
// sorting they applied. That has three properties a new endpoint would not:
//
//   * WYSIWYG. Exporting the filtered view is what "export" means to an
//     operator. A server export that ignores the on-screen filters silently
//     returns a different dataset, which is the disparity class this whole
//     audit is about.
//   * No new backend surface, so no new authz decision to get wrong.
//   * Works identically on all 26 lists, which is the point.
//
// It is NOT a replacement for the server-side Asset/Report exports: those
// paginate beyond what the page holds and carry formatting. Where one exists,
// keep it. This is for the 22 lists that had nothing.
import { DownloadIcon } from "lucide-react";

import { Button } from "@/components/ui/button";

export interface CsvColumn<T> {
  /** Header text, written out verbatim. */
  header: string;
  /** Cell value. Return null/undefined for an empty cell. */
  value: (row: T) => string | number | null | undefined;
}

/** RFC 4180 quoting.
 *
 * The leading-character guard is NOT cosmetic: a cell beginning `=`, `+`, `-`
 * or `@` is executed as a formula when the file is opened in Excel or Sheets.
 * This data includes operator-supplied camera names and alert titles, so it is
 * attacker-influenced in exactly the way CSV injection needs. Prefixing a
 * single quote neutralises it and is what spreadsheet software expects. */
export function csvCell(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return "";
  let s = String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/[",\n\r]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function toCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const head = columns.map((c) => csvCell(c.header)).join(",");
  const body = rows.map((r) => columns.map((c) => csvCell(c.value(r))).join(","));
  // CRLF + a UTF-8 BOM: Excel misreads a bare LF file as one long line and
  // mangles non-ASCII site names without the BOM.
  return "﻿" + [head, ...body].join("\r\n") + "\r\n";
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface Props<T> {
  rows: T[];
  columns: CsvColumn<T>[];
  /** Without .csv — a date stamp is appended. */
  filename: string;
  label?: string;
}

export function ExportCsvButton<T>({ rows, columns, filename, label }: Props<T>) {
  const stamp = new Date().toISOString().slice(0, 10);
  return (
    <Button
      variant="outline"
      size="sm"
      // Disabled on an empty list rather than handing over a header-only file
      // that looks like a successful export of nothing.
      disabled={rows.length === 0}
      title={
        rows.length === 0
          ? "Nothing to export"
          : `Export the ${rows.length} rows currently shown, with your filters applied`
      }
      onClick={() => downloadCsv(`${filename}-${stamp}`, toCsv(rows, columns))}
    >
      <DownloadIcon className="mr-1 h-3.5 w-3.5" />
      {label ?? "Export CSV"} ({rows.length})
    </Button>
  );
}
